import { useCallback } from 'react';
import { Box, Flex, HStack, Skeleton } from '@chakra-ui/react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import type { HTMLChakraProps } from '@chakra-ui/react';
import { useEstimatedDeliveryDate } from '../../../../bff-api/delivery/useEstimatedDeliveryDate';
import { GetIcon } from '../../../../components/icons';
import Info from '../../../../components/icons/icon-components/info';
import { usePDP } from '../../../../contexts/pdp/usePDP';
import { useEstimatedDeliveryMessage } from '../../../../hooks/useEstimatedDeliveryMessage';
import { useUserZipCode } from '../../../../hooks/useUserZipCode';
import { Button, Text, Tooltip, TooltipPlacement } from '../../../../ui-components';
import { useCustomPreferences } from '../../../../utils/api';
import { CustomPreference } from '../../../../utils/api/CustomPreference';
import type { Product } from '../../../../bff-api/products/model';

type ShipToBoxProps = {
    id: string;
    product: Product;
    onClickZipCode: () => void;
} & HTMLChakraProps<'div'>;

const shipFastMessage = defineMessages({
    shipFastTitle: {
        id: 'edd.non.drop.shipping.title',
        defaultMessage: 'Want it Faster?',
    },
    shipFastLinkCaption: {
        id: 'edd.non.drop.shipping.description',
        defaultMessage: 'See similar items that arrive sooner',
    },
});

export const ShipToBox: React.FC<ShipToBoxProps> = ({
    id,
    product,
    onClickZipCode,
    ...props
}: ShipToBoxProps) => {
    const intl = useIntl();
    const {
        inventoryStore: { flags },
        getElementRef,
    } = usePDP();
    const { data: zipCode } = useUserZipCode();

    const {
        eddEnable = true,
        enableShipFastPDP = false,
        shipFastPDPTitle = intl.formatMessage(shipFastMessage.shipFastTitle),
        shipFastPDPLinkCaption = intl.formatMessage(shipFastMessage.shipFastLinkCaption),
    } = useCustomPreferences([
        CustomPreference.EDD_ENABLE,
        CustomPreference.ENABLE_SHIP_FAST_PDP,
        CustomPreference.SHIP_FAST_PDP_TITLE,
        CustomPreference.SHIP_FAST_PDP_LINK_CAPTION,
    ]);

    const {
        inventory,
        c_dropShipEligibility,
        c_customDeliveryMessage,
        c_dropshipEstimatedDeliveryDate,
        c_dropshipEstimatedProcessingDate,
    } = product;

    const { data, isLoading } = useEstimatedDeliveryDate(zipCode);
    const { edd, postalCode, city } = data ?? {};

    const showDropShip = !!c_dropShipEligibility;
    const showBackorder = flags.isOnlyBackOrder && !showDropShip;
    const showSkeleton = isLoading && eddEnable && zipCode && !showDropShip && !showBackorder;
    const eddValid = !!edd && !!postalCode;
    const showEdd = !showBackorder && !showDropShip && eddEnable && eddValid && !showSkeleton;
    const showZipCode = !showBackorder && eddEnable && !eddValid && !showDropShip && !showSkeleton;
    const showShipsFast =
        !showEdd && !showDropShip && !eddEnable && !showSkeleton && enableShipFastPDP;

    const now = Date.now();
    const { message, startDate, endDate } = c_customDeliveryMessage ?? {};
    const showCustomDeliveryMessage =
        message && (startDate ? startDate <= now : false) && (endDate ? now <= endDate : false);
    const shippingMessage = useEstimatedDeliveryMessage({
        c_dropshipEstimatedDeliveryDate,
        c_dropshipEstimatedProcessingDate,
    });

    const handleOnShipFasterClick = useCallback(() => {
        const headerRef = getElementRef('layout-header').current;
        const recommendationsRef = getElementRef('recommendations-container').current;

        const to =
            window.scrollY +
            (recommendationsRef?.getBoundingClientRect()?.y ?? 0) -
            (headerRef?.getBoundingClientRect()?.height ?? 0);

        window.scrollTo({ top: to, behavior: 'smooth' });
    }, [getElementRef]);

    if (!showDropShip && !showBackorder && !showShipsFast && !showEdd && !showZipCode) {
        return null;
    }

    return (
        <Box {...props}>
            <Flex
                data-testid={id}
                direction="column"
                className="space-y-2"
                padding={showSkeleton ? 0 : 3}
                borderRadius={4}
                background="slt-grey.100"
                minH="78px"
                width="100%"
                justifyContent="center"
            >
                {showSkeleton ? <Skeleton id={`${id}-loading`} w="100%" h="100%" /> : null}

                {showEdd ? (
                    <>
                        <Flex
                            data-testid={`${id}-ship-to-description`}
                            gap={1}
                            className="flex-wrap items-center lg:flex-nowrap"
                        >
                            <Text
                                id={`${id}-ship-to-description-1`}
                                style="bold"
                                className="shrink-0"
                            >
                                <FormattedMessage
                                    id="edd.day.delivery"
                                    defaultMessage="{edd} day delivery"
                                    values={{ edd }}
                                />
                            </Text>
                            <Tooltip
                                label={intl.formatMessage({
                                    id: 'delivery_options.edd.info',
                                    defaultMessage:
                                        'Please allow up to one business day of processing time.',
                                })}
                                placement={TooltipPlacement.top}
                            >
                                <Info data-testid={`${id}-ship-to-description-info-icon`} />
                            </Tooltip>
                        </Flex>
                        <Flex data-testid={`${id}-ship-to`} className="space-x-1">
                            {city ? (
                                <>
                                    <Text
                                        id={`${id}-ship-to-label`}
                                        size="small"
                                        style="bold"
                                        textTransform="capitalize"
                                    >
                                        <FormattedMessage
                                            id="edd.ship.to"
                                            defaultMessage="Ship To {city}"
                                            values={{ city: city.toLowerCase() }}
                                        />
                                    </Text>

                                    <Text
                                        id={`${id}-ship-to-postal-code`}
                                        size="small"
                                        type="link"
                                        onClick={onClickZipCode}
                                    >
                                        {postalCode}
                                    </Text>
                                </>
                            ) : (
                                <>
                                    <Text id={`${id}-ship-to-label`} size="small" style="bold">
                                        <FormattedMessage
                                            id="edd.ship.to.simple"
                                            defaultMessage="Ship to"
                                        />
                                    </Text>

                                    <Text
                                        id={`${id}-ship-to-postal-code`}
                                        size="small"
                                        type="link"
                                        onClick={onClickZipCode}
                                    >
                                        {postalCode}
                                    </Text>
                                </>
                            )}
                        </Flex>
                    </>
                ) : null}

                {showDropShip ? (
                    <>
                        <Text id={`${id}-drop-shipping-label`} style="bold">
                            <FormattedMessage
                                id="edd.drop.shipping.from.vendor"
                                defaultMessage="Ships directly from vendor"
                            />
                        </Text>

                        {showCustomDeliveryMessage ? (
                            <Text id={`${id}-drop-shipping-value`}>
                                {showCustomDeliveryMessage ? message : null}
                            </Text>
                        ) : null}

                        {(c_dropshipEstimatedDeliveryDate || c_dropshipEstimatedProcessingDate) &&
                        !showCustomDeliveryMessage ? (
                            <Text>{shippingMessage}</Text>
                        ) : null}
                    </>
                ) : null}

                {showShipsFast ? (
                    <>
                        <Text id={`${id}-non-drop-shipping-label`} style="bold">
                            {shipFastPDPTitle}
                        </Text>
                        <HStack spacing={1}>
                            <GetIcon name="ships-fast" size="sm" />
                            <Button
                                variant="text-button-no-hover"
                                padding="0px"
                                onClick={handleOnShipFasterClick}
                            >
                                <Text>{shipFastPDPLinkCaption}</Text>
                            </Button>
                        </HStack>
                    </>
                ) : null}

                {showBackorder ? (
                    <>
                        <Text id={`${id}-backorder-label`} style="bold">
                            <FormattedMessage id="edd.backorder" defaultMessage="Backorder" />
                        </Text>

                        <Flex id={`${id}-backorder-value`} className="space-x-1">
                            <Text id={`${id}-backorder-value-label`} size="small">
                                <FormattedMessage
                                    id="edd.expected.stock.date"
                                    defaultMessage="The expected stock date is"
                                />
                            </Text>

                            <Text id={`${id}-backorder-value-date`} size="small" style="bold">
                                {new Date(inventory?.inStockDate as string).toDateString()}.
                            </Text>
                        </Flex>
                    </>
                ) : null}

                {showZipCode ? (
                    <Text id={`${id}-enter-zip-code`} type="link" onClick={onClickZipCode}>
                        <FormattedMessage
                            id="edd.enter.zip.code"
                            defaultMessage="Enter your Zip Code"
                        />
                    </Text>
                ) : null}
            </Flex>
        </Box>
    );
};
